import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, ChevronLeft, RefreshCw, Search, X, Printer, Download,
} from 'lucide-react';
import { vouchersApi } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import useChangePeriodKey from '../hooks/useChangePeriodKey';

const fmt = (n: any) =>
  Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n: any) =>
  Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 3 });

const toInputDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const displayDate = (s?: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};
const MONTHS_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_LONG = ['January','February','March','April','May','June','July','August','September','October','November','December'];

type MonthCol = { year: number; month: number };
type Cell = { value: number; qty: number; vch_count: number };
type CatRow = {
  category_id: number;
  category_name: string;
  months: Cell[];
  total: number;
  total_qty: number;
};
type ItemRow = {
  item_id: number | null;
  item_name: string;
  qty: number;
  vch_count: number;
  sales_value: number;
};

const STORAGE_KEY = 'category-sales-filters';

function fyBounds(d: Date) {
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return { from: new Date(y, 3, 1), to: new Date(y + 1, 2, 31) };
}
function defaultFilters() {
  const today = new Date();
  const { from } = fyBounds(today);
  return { dateFrom: toInputDate(from), dateTo: toInputDate(today) };
}
// Last day of a month — clamps the drill-down window so a click on the
// "May 2026" cell queries 01-May..31-May regardless of the outer range.
function monthBounds(year: number, month: number /* 1-12 */) {
  const from = new Date(year, month - 1, 1);
  const to = new Date(year, month, 0); // day 0 of next month = last day of this one
  return { from: toInputDate(from), to: toInputDate(to) };
}

export default function CategorySalesReport() {
  const navigate = useNavigate();
  const { showError } = useToast();

  const initial = (() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return { ...defaultFilters(), ...JSON.parse(saved) };
    } catch { /* ignore */ }
    return defaultFilters();
  })();

  const [dateFrom, setDateFrom] = useState<string>(initial.dateFrom);
  const [dateTo, setDateTo]     = useState<string>(initial.dateTo);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [months, setMonths] = useState<MonthCol[]>([]);
  const [rows, setRows] = useState<CatRow[]>([]);
  const [totals, setTotals] = useState<{ months: { value: number; qty: number }[]; total: number; total_qty: number }>(
    { months: [], total: 0, total_qty: 0 },
  );

  // Drill-down: one category × one month (or the whole row when month is null).
  const [drill, setDrill] = useState<{ category: CatRow; month: MonthCol | null } | null>(null);
  const [drillRows, setDrillRows] = useState<ItemRow[]>([]);
  const [drillTotals, setDrillTotals] = useState({ qty: 0, vch_count: 0, sales_value: 0 });
  const [drillLoading, setDrillLoading] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ dateFrom, dateTo })); } catch { /* ignore */ }
  }, [dateFrom, dateTo]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const [showControls, setShowControls] = useState(false);
  // Alt+F2 — Tally's Change Period: open the date filter, cursor in From.
  useChangePeriodKey(() => setShowControls(true));
  // No pagination here — every row is already in the DOM, so printing needs
  // no state to bypass a page slice the way the Sales Register does.
  const handlePrint = () => {
    if ((drill ? drillRows.length : rows.length) === 0) return;
    window.print();
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vouchersApi.getCategorySalesMonthly({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        search: debouncedSearch || undefined,
      });
      if (res.success) {
        setMonths(res.data.months || []);
        setRows(res.data.rows || []);
        setTotals({
          months: res.data.totals?.months || [],
          total: res.data.totals?.total || 0,
          total_qty: res.data.totals?.total_qty || 0,
        });
      }
    } catch { showError('Error', 'Failed to load Category-wise Sales'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, debouncedSearch, showError]);
  useEffect(() => { load(); }, [load]);

  const loadDrill = useCallback(async (category: CatRow, month: MonthCol | null) => {
    // A cell click narrows to that month; a row-total click keeps the full window.
    const range = month ? monthBounds(month.year, month.month) : { from: dateFrom, to: dateTo };
    setDrillLoading(true);
    try {
      const res = await vouchersApi.getCategorySalesItems(category.category_id, {
        date_from: range.from || undefined,
        date_to: range.to || undefined,
      });
      if (res.success) {
        setDrillRows(res.data.rows || []);
        setDrillTotals({
          qty: res.data.totals?.qty || 0,
          vch_count: res.data.totals?.vch_count || 0,
          sales_value: res.data.totals?.sales_value || 0,
        });
      }
    } catch { showError('Error', 'Failed to load item detail'); }
    finally { setDrillLoading(false); }
  }, [dateFrom, dateTo, showError]);
  useEffect(() => {
    if (drill) loadDrill(drill.category, drill.month);
  }, [drill, loadDrill]);

  const monthLabel = (m: MonthCol, long = false) =>
    `${(long ? MONTHS_LONG : MONTHS_ABBR)[m.month - 1]} ${long ? m.year : String(m.year).slice(2)}`;

  // Months with zero sales across every category are noise in a wide pivot —
  // the toggle lets the user collapse the grid to just the active months.
  const [hideEmptyMonths, setHideEmptyMonths] = useState(false);
  const visibleIdx = useMemo(() => {
    const all = months.map((_, i) => i);
    if (!hideEmptyMonths) return all;
    const active = all.filter(i => (totals.months[i]?.value || 0) !== 0);
    return active.length ? active : all;
  }, [months, totals, hideEmptyMonths]);

  const exportCsv = () => {
    if (rows.length === 0) return;
    const out: string[][] = [
      ['Item Category-wise Month-wise Sales Value'],
      [`From ${displayDate(dateFrom)} to ${displayDate(dateTo)}`],
      ['Values are item-line sales value (excludes GST, freight & round-off)'],
      [],
      ['Item Category', ...visibleIdx.map(i => monthLabel(months[i], true)), 'Total'],
    ];
    for (const r of rows) {
      out.push([r.category_name, ...visibleIdx.map(i => fmt(r.months[i]?.value)), fmt(r.total)]);
    }
    out.push(['Grand Total', ...visibleIdx.map(i => fmt(totals.months[i]?.value)), fmt(totals.total)]);
    const csv = out.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Category_Sales_${dateFrom}_to_${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const cell = 'border border-slate-300 px-2.5 py-1.5 text-[14px] leading-snug';
  const cellNum = `${cell} text-right tabular-nums whitespace-nowrap`;
  const headCell = 'border border-slate-400 bg-slate-200 px-2.5 py-1.5 text-[13px] font-bold text-slate-700 uppercase tracking-wide sticky top-0 z-10';

  return (
    <div className="flex flex-col w-full fixed left-0 right-0 top-14 bottom-16 sm:static sm:h-full sm:top-auto sm:bottom-auto" style={{ overscrollBehavior: 'contain' }}>
      {/* Print-only header */}
      <div className="print-only mb-3 px-3" aria-hidden>
        <h1 className="text-xl font-bold mb-1">Item Category-wise Sales Value</h1>
        <div className="text-sm">
          {drill
            ? `${drill.category.category_name}${drill.month ? ` — ${monthLabel(drill.month, true)}` : ''}`
            : `From ${displayDate(dateFrom)} to ${displayDate(dateTo)}`}
        </div>
      </div>

      {/* ── Header bar ── */}
      <div className="flex-none bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between print:hidden">
        <div className="flex items-center gap-1 min-w-0">
          <button
            onClick={() => drill
              ? (setDrill(null), setDrillRows([]), setShowControls(false))
              : navigate(-1)}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 -ml-1 flex-shrink-0"
            title={drill ? 'Back to categories' : 'Back'}>
            <ChevronLeft size={20} />
          </button>
          <h1 className="text-[17px] font-bold text-slate-800 truncate">Category-wise Sales</h1>
          {drill && (
            <span className="text-[13px] font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded flex-shrink-0 ml-1 truncate">
              {drill.category.category_name}{drill.month ? ` · ${monthLabel(drill.month)}` : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!drill && (
            <button onClick={() => setShowControls(s => !s)}
              className={`p-2 rounded-lg transition-colors ${showControls ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
              title="Search & filters (Alt+F2)">
              <Search size={18} />
            </button>
          )}
          {!drill && (
            <button onClick={exportCsv} disabled={rows.length === 0}
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40"
              title="Export CSV">
              <Download size={16} />
            </button>
          )}
          <button onClick={handlePrint}
            disabled={(drill ? drillRows.length : rows.length) === 0}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40"
            title="Print">
            <Printer size={16} />
          </button>
          <button onClick={drill ? () => loadDrill(drill.category, drill.month) : load}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"
            title="Refresh">
            <RefreshCw size={16} className={(loading || drillLoading) ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* ── Search & date filter panel ── */}
      {showControls && !drill && (
        <div className="flex-none bg-slate-50 border-b border-slate-200 px-3 py-2.5 print:hidden">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[140px]">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Category…" autoFocus
                className="pl-9 pr-7 py-2 border border-slate-300 rounded-lg text-[13px] w-full outline-none focus:ring-2 focus:ring-blue-300 bg-white" />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X size={12} />
                </button>
              )}
            </div>
            <div className="flex items-stretch border border-slate-300 rounded-lg bg-white overflow-hidden">
              <div className="flex items-center gap-1 px-2 border-r border-slate-300 bg-slate-100">
                <Calendar size={12} className="text-slate-400" />
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wide">From</span>
              </div>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="text-[13px] text-slate-700 outline-none bg-transparent px-2 py-1.5 w-[115px]" />
              <div className="flex items-center px-2 border-l border-r border-slate-300 bg-slate-100">
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wide">To</span>
              </div>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="text-[13px] text-slate-700 outline-none bg-transparent px-2 py-1.5 w-[115px]" />
            </div>
            <label className="flex items-center gap-1.5 text-[12px] text-slate-600 select-none cursor-pointer px-1">
              <input type="checkbox" checked={hideEmptyMonths}
                onChange={e => setHideEmptyMonths(e.target.checked)}
                className="rounded border-slate-300" />
              Hide empty months
            </label>
          </div>
        </div>
      )}

      {!drill ? (
        <PivotView
          months={months} visibleIdx={visibleIdx} rows={rows} totals={totals}
          loading={loading} dateFrom={dateFrom} dateTo={dateTo}
          monthLabel={monthLabel}
          onPickCell={(category, month) => setDrill({ category, month })}
          headCell={headCell} cell={cell} cellNum={cellNum}
        />
      ) : (
        <DrillView
          drill={drill} rows={drillRows} totals={drillTotals} loading={drillLoading}
          monthLabel={monthLabel}
          headCell={headCell} cell={cell} cellNum={cellNum}
        />
      )}
    </div>
  );
}

/* ── Pivot grid: categories down, months across ────────────────────────── */
function PivotView({
  months, visibleIdx, rows, totals, loading, dateFrom, dateTo,
  monthLabel, onPickCell, headCell, cell, cellNum,
}: {
  months: MonthCol[]; visibleIdx: number[]; rows: CatRow[];
  totals: { months: { value: number; qty: number }[]; total: number; total_qty: number };
  loading: boolean; dateFrom: string; dateTo: string;
  monthLabel: (m: MonthCol, long?: boolean) => string;
  onPickCell: (category: CatRow, month: MonthCol | null) => void;
  headCell: string; cell: string; cellNum: string;
}) {
  const colCount = visibleIdx.length + 2;
  const empty = !loading && rows.length === 0;

  return (
    <>
      {/* Mobile — one card per category, months listed inside. A wide pivot
          cannot work on a phone, so the axes are stacked instead of crossed. */}
      <div className="sm:hidden flex-1 min-h-0 overflow-auto bg-white" style={{ overscrollBehavior: 'contain' }}>
        {loading ? (
          <div className="text-center text-gray-400 py-8">Loading…</div>
        ) : empty ? (
          <div className="text-center text-gray-400 py-8">No sales between {displayDate(dateFrom)} and {displayDate(dateTo)}</div>
        ) : (
          rows.map((r) => (
            <div key={r.category_id} className="border-b border-gray-200">
              <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50"
                onClick={() => onPickCell(r, null)}>
                <span className="font-bold text-gray-900 text-[15px]">{r.category_name}</span>
                <span className="text-[15px] font-semibold text-blue-700 tabular-nums">{fmt(r.total)}</span>
              </div>
              <div className="divide-y divide-gray-100">
                {visibleIdx
                  .filter(i => (r.months[i]?.value || 0) !== 0)
                  .map(i => (
                    <div key={i} className="flex items-center justify-between px-6 py-2 active:bg-blue-50"
                      onClick={() => onPickCell(r, months[i])}>
                      <span className="text-[13px] text-gray-600">{monthLabel(months[i], true)}</span>
                      <span className="text-[14px] text-gray-900 tabular-nums">{fmt(r.months[i]?.value)}</span>
                    </div>
                  ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Mobile — grand total bar */}
      {!loading && rows.length > 0 && (
        <div className="sm:hidden flex-none border-t border-gray-200 print:hidden">
          <div className="bg-blue-700 text-white px-4 py-2.5 flex justify-between items-center">
            <span className="font-bold text-sm tracking-widest">GRAND TOTAL</span>
            <span className="text-sm tabular-nums font-semibold">{fmt(totals.total)}</span>
          </div>
        </div>
      )}

      {/* Desktop — the pivot proper. The Particulars column is sticky so the
          category stays readable while scrolling a 12-month window sideways. */}
      <div className="hidden sm:block flex-1 min-h-0 overflow-auto bg-white border-x border-b border-slate-300 mx-3 mb-3 print:mx-0 print:overflow-visible print:max-h-none print:border-slate-400">
        <table className="border-collapse text-[14px] w-full">
          <thead>
            <tr>
              <th className={`${headCell} text-left sticky left-0 z-20 min-w-[200px]`}>Item Category</th>
              {visibleIdx.map(i => (
                <th key={i} className={`${headCell} text-right min-w-[110px]`}>{monthLabel(months[i])}</th>
              ))}
              <th className={`${headCell} text-right min-w-[130px] bg-slate-300`}>Total</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={colCount} className={`${cell} text-center text-slate-400 py-6`}>Loading…</td></tr>
            ) : empty ? (
              <tr><td colSpan={colCount} className={`${cell} text-center text-slate-400 py-6`}>
                No sales between {displayDate(dateFrom)} and {displayDate(dateTo)}
              </td></tr>
            ) : (
              rows.map((r, ri) => {
                const zebra = ri % 2 === 1 ? 'bg-slate-50' : 'bg-white';
                return (
                  <tr key={r.category_id} className={`${zebra} text-slate-800 hover:bg-blue-50`}>
                    <td className={`${cell} font-medium sticky left-0 z-10 ${zebra}`}>
                      <button onClick={() => onPickCell(r, null)}
                        className="text-left hover:text-blue-700 hover:underline decoration-dotted">
                        {r.category_name}
                      </button>
                    </td>
                    {visibleIdx.map(i => {
                      const c = r.months[i];
                      const v = c?.value || 0;
                      return (
                        <td key={i}
                          className={`${cellNum} ${v === 0 ? 'text-slate-300' : 'cursor-pointer hover:bg-blue-100'}`}
                          onClick={() => { if (v !== 0) onPickCell(r, months[i]); }}
                          title={v !== 0 ? `${fmtQty(c.qty)} qty · ${c.vch_count} vouchers` : undefined}>
                          {v === 0 ? '—' : fmt(v)}
                        </td>
                      );
                    })}
                    <td className={`${cellNum} font-bold text-blue-800 bg-blue-50/60`}>{fmt(r.total)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
          {!loading && rows.length > 0 && (
            <tfoot>
              <tr className="bg-slate-200 font-bold text-slate-900">
                <td className={`${cell} sticky left-0 z-10 bg-slate-200 uppercase tracking-wide`}>Grand Total</td>
                {visibleIdx.map(i => (
                  <td key={i} className={cellNum}>{fmt(totals.months[i]?.value)}</td>
                ))}
                <td className={`${cellNum} bg-slate-300`}>{fmt(totals.total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Scope note — the number people will otherwise try to reconcile against
          the gross Sales Register figure and find short by exactly the GST. */}
      {!loading && rows.length > 0 && (
        <div className="hidden sm:block flex-none px-3 pb-2 -mt-1 text-[11px] text-slate-400 print:px-0">
          Item-line sales value — excludes GST, freight and round-off, which are voucher-level and belong to no category.
        </div>
      )}
    </>
  );
}

/* ── Drill-down: the item lines behind one cell ─────────────────────────── */
function DrillView({
  drill, rows, totals, loading, monthLabel, headCell, cell, cellNum,
}: {
  drill: { category: CatRow; month: MonthCol | null };
  rows: ItemRow[]; totals: { qty: number; vch_count: number; sales_value: number };
  loading: boolean;
  monthLabel: (m: MonthCol, long?: boolean) => string;
  headCell: string; cell: string; cellNum: string;
}) {
  const scope = drill.month ? monthLabel(drill.month, true) : 'the selected period';
  return (
    <>
      {/* Mobile */}
      <div className="sm:hidden flex-1 min-h-0 overflow-auto bg-white" style={{ overscrollBehavior: 'contain' }}>
        {loading ? (
          <div className="text-center text-gray-400 py-8">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center text-gray-400 py-8">No items sold in {scope}</div>
        ) : (
          rows.map((r, i) => (
            <div key={`${r.item_id ?? 'x'}-${i}`} className="border-b border-gray-200 px-4 py-3 flex items-start justify-between">
              <div className="min-w-0 pr-3">
                <div className="font-medium text-gray-900 text-[14px] truncate">{r.item_name}</div>
                <div className="text-[12px] text-gray-500 mt-0.5">
                  {fmtQty(r.qty)} qty · {r.vch_count} vouchers
                </div>
              </div>
              <span className="text-[15px] font-medium text-gray-900 tabular-nums flex-shrink-0">{fmt(r.sales_value)}</span>
            </div>
          ))
        )}
      </div>
      {!loading && rows.length > 0 && (
        <div className="sm:hidden flex-none border-t border-gray-200 print:hidden">
          <div className="bg-blue-700 text-white px-4 py-2.5 flex justify-between items-center">
            <span className="font-bold text-sm tracking-widest">TOTAL</span>
            <span className="text-sm tabular-nums font-semibold">{fmt(totals.sales_value)}</span>
          </div>
        </div>
      )}

      {/* Desktop */}
      <div className="hidden sm:block flex-1 min-h-0 overflow-auto bg-white border-x border-b border-slate-300 mx-3 mb-3 print:mx-0 print:overflow-visible print:max-h-none print:border-slate-400">
        <table className="border-collapse text-[14px] w-full">
          <thead>
            <tr>
              <th className={`${headCell} text-left`}>Item</th>
              <th className={`${headCell} text-right w-32`}>Qty</th>
              <th className={`${headCell} text-right w-32`}>Vouchers</th>
              <th className={`${headCell} text-right w-44`}>Sales Value</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className={`${cell} text-center text-slate-400 py-6`}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={4} className={`${cell} text-center text-slate-400 py-6`}>No items sold in {scope}</td></tr>
            ) : (
              rows.map((r, i) => (
                <tr key={`${r.item_id ?? 'x'}-${i}`} className={i % 2 === 1 ? 'bg-slate-50' : 'bg-white'}>
                  <td className={`${cell} font-medium`}>{r.item_name}</td>
                  <td className={cellNum}>{fmtQty(r.qty)}</td>
                  <td className={cellNum}>{r.vch_count}</td>
                  <td className={cellNum}>{fmt(r.sales_value)}</td>
                </tr>
              ))
            )}
          </tbody>
          {!loading && rows.length > 0 && (
            <tfoot>
              <tr className="bg-slate-200 font-bold text-slate-900">
                <td className={`${cell} uppercase tracking-wide`}>Total</td>
                <td className={cellNum}>{fmtQty(totals.qty)}</td>
                <td className={cellNum}>{totals.vch_count}</td>
                <td className={cellNum}>{fmt(totals.sales_value)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
}
